/**
 * Prayer-times client service. All upstream calls go through the backend
 * proxy so we never expose 3rd-party endpoints to the browser and to
 * benefit from server-side caching.
 *
 * Local cache (IndexedDB-via-localStorage fallback):
 *   - timings per (lat,lng,method,school,DD-MM-YYYY) for the day
 *   - last successful location object
 *   - last user preferences (offline-first; synced to server when online)
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LS_PREFS    = 'noor_prayer_prefs';
const LS_TIMINGS  = 'noor_prayer_timings_v1';

export const PRAYER_METHODS = [
  { id: 3,  label: 'Muslim World League' },
  { id: 5,  label: 'Egyptian General Authority' },
  { id: 4,  label: 'Umm Al-Qura (Makkah)' },
  { id: 2,  label: 'ISNA (Amérique du Nord)' },
  { id: 1,  label: 'University of Karachi' },
  { id: 12, label: 'France / UOIF (18°)' },
  { id: 13, label: 'Diyanet (Turquie)' },
  { id: 7,  label: 'Tehran' },
];

export const SCHOOLS = [
  { id: 0, label: 'Shafi (Maliki, Hanbali)' },
  { id: 1, label: 'Hanafi' },
];

/* -------- local cache helpers ----------------------------------------- */
function loadLs(key) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; }
  catch { return null; }
}
function saveLs(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
}

function dayKey(date = new Date()) {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}

function cacheKey(lat, lng, method, school, date) {
  return `${lat.toFixed(4)}_${lng.toFixed(4)}_${method}_${school}_${date}`;
}

/* -------- preferences -------------------------------------------------- */
function authHeader() {
  const t = localStorage.getItem('noor_albayan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export const prayerTimesService = {
  /* local prefs (offline-first) */
  getLocalPrefs() {
    const p = loadLs(LS_PREFS) || { method: 12, school: 0, last_location: null };
    if (!Array.isArray(p.favorite_locations)) p.favorite_locations = [];
    return p;
  },
  saveLocalPrefs(prefs) {
    saveLs(LS_PREFS, prefs);
  },

  /** Sync prefs from server (when user is logged in) and merge with local. */
  async loadRemotePrefs() {
    try {
      const r = await axios.get(`${API_URL}/api/me/prayer-preferences`, { headers: authHeader() });
      const remote = r.data || {};
      const local = this.getLocalPrefs();
      const merged = { ...local, ...remote };
      this.saveLocalPrefs(merged);
      return merged;
    } catch {
      return this.getLocalPrefs();
    }
  },

  async pushRemotePrefs(prefs) {
    try {
      await axios.put(`${API_URL}/api/me/prayer-preferences`, prefs, { headers: authHeader() });
    } catch { /* offline — local cache kept */ }
  },

  /* timings -------------------------------------------------------------- */
  async getTimings({ lat, lng, method = 12, school = 0, date }) {
    const dk = date || dayKey();
    const ck = cacheKey(lat, lng, method, school, dk);

    // 1) local cache hit?
    const allCache = loadLs(LS_TIMINGS) || {};
    if (allCache[ck]) return { ...allCache[ck], source: 'local' };

    // 2) live call via backend
    const r = await axios.get(`${API_URL}/api/prayer-times/timings`, {
      params: { lat, lng, method, school, date: dk },
      timeout: 12000,
    });
    const payload = r.data;
    // 3) update cache (prune > 14 days)
    const now = Date.now();
    const pruned = Object.fromEntries(
      Object.entries(allCache).filter(([_, v]) => (now - (v?._cachedAt || 0)) < 14 * 86400000),
    );
    pruned[ck] = { ...payload, _cachedAt: now };
    saveLs(LS_TIMINGS, pruned);
    return payload;
  },

  /* geocoding ----------------------------------------------------------- */
  async geocode(query) {
    if (!query || query.trim().length < 2) return [];
    const r = await axios.get(`${API_URL}/api/prayer-times/geocode`, {
      params: { q: query.trim() }, timeout: 10000,
    });
    return r.data?.results || [];
  },

  async reverseGeocode(lat, lng) {
    const r = await axios.get(`${API_URL}/api/prayer-times/reverse`, {
      params: { lat, lng }, timeout: 10000,
    });
    return r.data;
  },

  /** Cached adhan-sounds catalog. */
  _soundCatalog: null,
  async getAdhanSounds() {
    if (this._soundCatalog) return this._soundCatalog;
    try {
      const r = await axios.get(`${API_URL}/api/prayer-times/adhan-sounds`, { timeout: 6000 });
      this._soundCatalog = r.data?.sounds || [];
    } catch {
      this._soundCatalog = [];
    }
    return this._soundCatalog;
  },
};

/* -------- next prayer logic ------------------------------------------- */
export const PRAYER_KEYS = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
export const PRAYER_LABEL_FR = {
  Fajr: 'Fajr', Sunrise: 'Lever du soleil', Dhuhr: 'Dhuhr',
  Asr: 'Asr', Maghrib: 'Maghrib', Isha: 'Isha',
};

export const ALERT_MODES = [
  { id: 'off',     label: 'Désactivé' },
  { id: 'silent',  label: 'Silencieux' },
  { id: 'vibrate', label: 'Vibreur' },
  { id: 'adhan',   label: 'Adhan' },
];

export function defaultAlertsAllOff() {
  return Object.fromEntries(PRAYER_KEYS.map((k) => [k, { mode: 'off', sound_id: 'mecca', iqama_delay_min: 0 }]));
}

/** Build the list of upcoming-prayer objects (today + tomorrow morning). */
export function buildPrayerObjects(timings, now = new Date()) {
  if (!timings) return [];
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const out = [];
  for (const k of PRAYER_KEYS) {
    const t = (timings[k] || '').split(' ')[0];
    const [h, m] = t.split(':').map((x) => parseInt(x, 10));
    if (Number.isNaN(h)) continue;
    const at = new Date(base);
    at.setHours(h, m || 0, 0, 0);
    if (at.getTime() < now.getTime()) {
      // Already passed today — push to tomorrow so the scheduler still picks it up next cycle.
      at.setDate(at.getDate() + 1);
    }
    out.push({ key: k, label: PRAYER_LABEL_FR[k], at });
  }
  return out.sort((a, b) => a.at - b.at);
}

export function computeNextPrayer(timings, now = new Date()) {
  if (!timings) return null;
  // Timings come in "HH:MM" — assume local timezone returned by AlAdhan
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const candidates = PRAYER_KEYS.map((k) => {
    const t = (timings[k] || '').split(' ')[0]; // strip "(CET)" suffixes
    const [h, m] = t.split(':').map((x) => parseInt(x, 10));
    const at = new Date(today);
    at.setHours(h || 0, m || 0, 0, 0);
    return { key: k, label: PRAYER_LABEL_FR[k], at };
  });
  const upcoming = candidates.find((c) => c.at.getTime() > now.getTime());
  if (upcoming) {
    return { ...upcoming, msUntil: upcoming.at.getTime() - now.getTime() };
  }
  // After Isha — next is tomorrow's Fajr
  const first = candidates[0];
  const tomorrow = new Date(first.at.getTime() + 24 * 3600 * 1000);
  return {
    key: first.key, label: first.label, at: tomorrow,
    msUntil: tomorrow.getTime() - now.getTime(),
  };
}

export function formatCountdown(ms) {
  if (ms < 0) ms = 0;
  const total = Math.floor(ms / 1000);
  const h = String(Math.floor(total / 3600)).padStart(2, '0');
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

/**
 * Return the prayer key whose adhan is currently being played, or null.
 *
 * A prayer is considered "active" when `now` is within the
 * `[at, at + windowMs)` window, where `at` is the prayer time on the
 * current day. The default window of 5 minutes mirrors a typical adhan
 * duration so the UI highlight feels grounded in real-world timing.
 *
 * Returns `{ key, msSinceStart, msRemaining }` or `null`.
 */
export function computeActiveAdhan(timings, now = new Date(), windowMs = 5 * 60 * 1000) {
  if (!timings) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (const k of PRAYER_KEYS) {
    if (k === 'Sunrise') continue; // No adhan at Sunrise.
    const t = (timings[k] || '').split(' ')[0];
    const [h, m] = t.split(':').map((x) => parseInt(x, 10));
    if (Number.isNaN(h)) continue;
    const at = new Date(today);
    at.setHours(h, m || 0, 0, 0);
    const delta = now.getTime() - at.getTime();
    if (delta >= 0 && delta < windowMs) {
      return {
        key: k,
        label: PRAYER_LABEL_FR[k],
        at,
        msSinceStart: delta,
        msRemaining: windowMs - delta,
      };
    }
  }
  return null;
}
